'use client';

import type { CSSProperties, ReactNode } from 'react';
import { Children, isValidElement, useEffect, useMemo, useState } from 'react';

import styles from './carousel.module.scss';

export type CarouselBreakpoint = {
  /**
   * Applies when the viewport width is less than or equal to this value.
   */
  maxWidth: number;
  /**
   * Number of slides to render at once when the breakpoint is active.
   */
  slidesPerView: number;
};

export type CarouselProps = {
  children: ReactNode;
  className?: string;
  /**
   * Default slides per view used above any provided breakpoint.
   */
  slidesPerView?: number;
  /**
   * Breakpoint configuration sorted from smallest to largest maxWidth.
   */
  breakpoints?: CarouselBreakpoint[];
  /**
   * Number of slides to advance with each navigation click.
   */
  slidesPerScroll?: number;
  showDots?: boolean;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
};

const DEFAULT_BREAKPOINTS: CarouselBreakpoint[] = [
  { maxWidth: 767, slidesPerView: 1 },
  { maxWidth: 1023, slidesPerView: 2 }
];

const DEFAULT_SLIDES_PER_VIEW = 3;

const resolveSlidesPerView = (
  width: number,
  baseSlides: number,
  breakpoints: CarouselBreakpoint[]
) => {
  const sorted = [...breakpoints].sort((a, b) => a.maxWidth - b.maxWidth);
  for (const breakpoint of sorted) {
    if (width <= breakpoint.maxWidth) {
      return breakpoint.slidesPerView;
    }
  }
  return baseSlides;
};

export const Carousel = ({
  children,
  className,
  slidesPerView = DEFAULT_SLIDES_PER_VIEW,
  breakpoints = DEFAULT_BREAKPOINTS,
  slidesPerScroll = 1,
  showDots = true,
  prevAriaLabel = 'Previous slide',
  nextAriaLabel = 'Next slide'
}: CarouselProps) => {
  const items = Children.toArray(children).filter(Boolean);
  // Use a stable initial value (1280) for both server and client to prevent
  // hydration mismatch. The real window width is synced in useEffect below.
  const [windowWidth, setWindowWidth] = useState(1280);
  const [currentIndex, setCurrentIndex] = useState(0);

  const activeSlidesPerView = useMemo(
    () => resolveSlidesPerView(windowWidth, slidesPerView, breakpoints),
    [windowWidth, slidesPerView, breakpoints]
  );

  const maxIndex = Math.max(0, items.length - activeSlidesPerView);

  useEffect(() => {
    // Sync to actual window width on mount, then track resizes
    setWindowWidth(window.innerWidth);

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - slidesPerScroll, 0));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + slidesPerScroll, maxIndex));
  };

  const goTo = (index: number) => {
    setCurrentIndex(() => Math.min(Math.max(index, 0), maxIndex));
  };

  if (!items.length) {
    return null;
  }

  const slidesStyle = {
    '--slides-per-view': activeSlidesPerView,
    transform: `translateX(-${(currentIndex * 100) / activeSlidesPerView}%)`
  } as CSSProperties & { '--slides-per-view'?: number };

  const mergedClassName = [styles.carousel, className]
    .filter(Boolean)
    .join(' ');
  const showNav = items.length > activeSlidesPerView;
  const dotCount = Math.max(1, items.length - activeSlidesPerView + 1);

  return (
    <div className={mergedClassName}>
      <div className={styles.frame}>
        {showNav && (
          <button
            type="button"
            className={styles.navButton}
            onClick={handlePrev}
            disabled={currentIndex === 0}
            aria-label={prevAriaLabel}
          >
            &#8249;
          </button>
        )}
        <div className={styles.viewport}>
          <div className={styles.slides} style={slidesStyle}>
            {items.map((item, index) => (
              <div
                className={styles.slide}
                key={
                  isValidElement(item) && item.key != null ? item.key : index
                }
              >
                <div className={styles.slideContent}>{item}</div>
              </div>
            ))}
          </div>
        </div>
        {showNav && (
          <button
            type="button"
            className={styles.navButton}
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
            aria-label={nextAriaLabel}
          >
            &#8250;
          </button>
        )}
      </div>
      {showDots && showNav && (
        <div className={styles.dots}>
          {Array.from({ length: dotCount }, (_, index) => (
            <button
              type="button"
              key={`carousel-dot-${index}`}
              className={
                index === currentIndex
                  ? `${styles.dot} ${styles.dotActive}`
                  : styles.dot
              }
              onClick={() => goTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
